// HomeIQ Report client. Single integration point between the React app and
// the FastAPI report endpoints. Returns the same shape the backend documents.
//
// Error contract
// ==============
// Success:      returns backend JSON { report_id, pdf_url, print_url, ... }
// User error:   throws Error with `.status` (4xx) and a clean message ready
//               to display to a user.
// System error: throws Error with `.status` (5xx or 0) and a friendly
//               fallback message. The raw response body is NEVER surfaced —
//               Cloudflare 502 HTML pages, tracebacks, and unparsed text
//               are all swallowed into a single user-facing sentence.

const API = process.env.REACT_APP_BACKEND_URL;

// Deliberately generic — the user does not need to see the difference
// between "Chromium missing" and "Mongo dropped a connection".
const GENERIC_SYSTEM_MESSAGE =
    "We couldn't generate your report right now. Please try again in a moment — if the issue persists, our team has been notified.";

const GENERIC_NETWORK_MESSAGE =
    "We couldn't reach the report service. Please check your connection and try again.";

/** Turn any fetch failure into a clean user-facing Error. */
async function parseFailureResponse(res) {
    const contentType = (res.headers.get("content-type") || "").toLowerCase();

    // JSON body — the backend's own error path. Surface its `detail` if it
    // reads like a human sentence; fall back to the generic system message
    // if it looks like a stack trace or missing binary hint.
    if (contentType.includes("application/json")) {
        try {
            const j = await res.json();
            const detail = (j && (j.detail || j.message)) || "";
            if (looksUserFriendly(detail)) return detail;
        } catch { /* fall through */ }
    }

    // Anything else — HTML (Cloudflare 502, gateway timeout, WAF blocks),
    // plain text (nginx error), empty — replace with a generic message.
    // We deliberately never propagate the raw body.
    return res.status >= 500
        ? GENERIC_SYSTEM_MESSAGE
        : `Request failed (${res.status}).`;
}

// Heuristic: a "user-friendly" backend detail is short and does not look
// like a Python stack trace, a binary-not-found error, or a file path.
function looksUserFriendly(detail) {
    if (!detail || typeof detail !== "string") return false;
    if (detail.length > 240) return false;
    const suspicious = ["Traceback", "Executable doesn't exist", "at /", "playwright._impl", "MongoServerError"];
    return !suspicious.some((s) => detail.includes(s));
}

export async function generateReport({ lead, snapshot }) {
    let res;
    try {
        res = await fetch(`${API}/api/reports/generate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lead, snapshot }),
        });
    } catch {
        // Network-layer failure (DNS, offline, CORS, aborted).
        const err = new Error(GENERIC_NETWORK_MESSAGE);
        err.status = 0;
        throw err;
    }

    if (!res.ok) {
        const message = await parseFailureResponse(res);
        const err = new Error(message);
        err.status = res.status;
        throw err;
    }

    try {
        return await res.json();
    } catch {
        // 2xx but non-JSON is theoretically possible (e.g. gateway rewrite).
        // Treat it as a system failure.
        const err = new Error(GENERIC_SYSTEM_MESSAGE);
        err.status = 502;
        throw err;
    }
}

export async function fetchReportSnapshot(reportId) {
    const res = await fetch(`${API}/api/reports/${reportId}/snapshot`);
    if (!res.ok) throw new Error(`Snapshot fetch failed (${res.status})`);
    return res.json();
}

export function reportPdfUrl(reportId) {
    return `${API}/api/reports/${reportId}/pdf`;
}
